import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SubHeader from '../../../components/SubHeader';
import ConfirmModal from '@/components/ConfirmModal';
import { deleteGroup, getGroups } from "@/api/groups";
import { useLoadingStore } from "@/stores/loading";
import { MESSAGE_NO_E37, MESSAGE_NO_E39, PERMISSION_ROLL_SYSADMIN } from "@/config";
import { useUserStore } from "@/stores/user";
import { useProjectStore } from "@/stores/project";
import { useGroupStore, GroupListHeader } from "@/stores/group";
import {SortIndicator, SortParams} from "@/components/SortIndicator";
import qs from 'qs';

interface Group {
  id: number,
  name: string,
  display_name: string,
}

interface LastUpdated {
  last_updated_by?: string,
  last_updated_at?: string,
}

export type SortPostParams = {
  sort_column_name: string | null,
  sort_order: number | null,
}

export default function GroupList() {
  const navigate = useNavigate();
  const setLoading = useLoadingStore(state => state.setLoading);
  const user = useUserStore(state => state.user);
  const [
    selectedProject,
    selectedAdminCategory,
  ] = useProjectStore(state => [
    state.project,
    state.adminCategory,
  ]);

  const [isShowModal, setIsShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [groups, setGroups] = useState<Group[]>([]);
  const [lastUpdated, setLastUpdated] = useState<LastUpdated>();
  const [isShowErrorModal, setIsShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [groupQueryParams, setGroupQueryParams, setDoSort, sortParams, setSortParams] = useGroupStore(state => [
    state.queryParams,
    state.setQueryParams,
    state.setDoSort,
    state.sortParams,
    state.setSortParams
  ]);
  const [searchQueryParam, setSearchQueryParam] = useState<any>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();

  const breadCrumbs = [
    { title: selectedProject?.name },
    { title: selectedAdminCategory?.name },
    { title: 'グループ一覧' }
  ];

  function getData(params: SortPostParams) {
    setLoading(true);
    const postParams: { [key: string]: SortPostParams; } = {
      'params': params,
    };

    const response = getGroups(postParams);
    response.then(result => {
      setGroups(result.data.groups);
      setLastUpdated(result.data.last_updated);

      setSearchQueryParam(result.data.params);
      setSearchParams(qs.stringify(params));
      setGroupQueryParams(params);
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E39);
    }).finally(() => {
      setLoading(false);
    });
  }

  useEffect(() => {
    if (user?.roll !== PERMISSION_ROLL_SYSADMIN) {
      navigate('/admin');
    }
    const sortParam = {
      item_name: null,
      order: null,
    }
    getData({
      ...groupQueryParams,
      sort_column_name: sortParam.item_name,
      sort_order: sortParam.order
    });
  }, [])

  const onClickNew = () => {
    navigate("/admin/groups/new");
  }

  const openDeleteGroupModal = ( id: number ) => {
    setIsShowModal(true);
    setDeleteId(id);
  }

  const submitDeleteGroup = () => {
    setLoading(true);
    const response = deleteGroup(deleteId);
    response.then(result => {
      setGroups(result.data.groups);
      setLastUpdated(result.data.last_updated);
      setIsShowModal(false);
      console.log(deleteId + "を削除しました")
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E37);
    }).finally(() => {
      setLoading(false);
    });
  }

  const onSort = useCallback((sortParam: SortParams) => {
    if (sortParam.item_name) {
      getData({
        ...groupQueryParams,
        sort_column_name: sortParam.item_name,
        sort_order: sortParam.order
      });
    }
  }, [groupQueryParams]);

  useEffect(() => {
    // URLパラメータから検索条件を取得し、状態に反映する
    const params = qs.parse(searchParams.toString(), {ignoreQueryPrefix: true});
  }, [searchParams]);

  useEffect(() => {
    if (groupQueryParams) {
      setSearchQueryParam(groupQueryParams);
      getData(groupQueryParams);
    }
  }, []);

  useEffect(() => {
    if (errorMessage) {
      setIsShowErrorModal(true);
    }
  }, [errorMessage])

  useEffect(() => {
    setDoSort(onSort);
    return () => {
      setDoSort(() => {
      });
    };
  }, [onSort]);

  return (
    <div>
      <div className="sub-header">
        <SubHeader breadCrumbs={breadCrumbs} title="グループ管理"/>
        <div className="uk-text-right uk-margin-small-bottom">
          最終更新ユーザ：{lastUpdated?.last_updated_by} 最終更新:{lastUpdated?.last_updated_at}
        </div>
        <div className="uk-flex-between uk-flex uk-flex-middle">
          <div></div>
          <button className="uk-button--m uk-button-refer uk-margin-left" onClick={onClickNew}>新規作成
          </button>
        </div>
      </div>
      <div className="container">
        <div className="group-tbl-wrap">
          <table className="group-tbl">
            <thead>
            <tr>
              <th>
                <div className="border-box"/>
                No.
              </th>
              {GroupListHeader.map((column, index) =>
                <th key={`list-header-${index}`} className="sort-indicator-wrap">
                  <div className="border-box"/>
                  {column.headerName}
                  {column.sortItemName != '' &&
                      <SortIndicator item_name={column.sortItemName} isGroup={true}/>
                  }
                </th>
              )}
               <th>
                <div className="border-box"/>
                削除
              </th>
            </tr>
            </thead>
            <tbody>
            {groups.map(( item, index ) => (
              <tr key={index}>
                <td className="uk-text-center">
                  <div className="border-box"/>
                  {index + 1}</td>
                <td className="uk-text-left">
                  <div className="border-box"/>
                  <Link to={`/admin/groups/${item.id}/edit`}>{item.id}：{item.name}</Link></td>
                <td className="uk-text-left">
                  <div className="border-box"/>
                    {item.display_name}
                  </td>
                <td className="uk-text-center">
                  <div className="border-box"/>
                  <a onClick={() => {
                    openDeleteGroupModal(item.id)
                  }}>削除</a>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmModal
        text="削除してもよろしいですか？"
        confirmButtonText="削除"
        isShow={isShowModal}
        onConfirm={() => {
          submitDeleteGroup();
        }}
        onCancel={() => {
          setIsShowModal(false);
        }}
      />
      {errorMessage &&
          <ConfirmModal
              text={errorMessage}
              confirmButtonText="OK"
              isShow={isShowErrorModal}
              onConfirm={() => {
                setIsShowErrorModal(false);
                setErrorMessage(null);
              }}
          />
      }
    </div>
  );
}